import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import EpeService from "../../../../services/EpeService";
import useEpeAccess from "../../../../hooks/useEpeAccess";
import { getErrorMessage, listJoin } from "../../../../helpers/functions";
import COLORS from "../../../../assets/Colors";
import theme from "../../../../assets/theme";
import EpeDialog, {
  DialogNotice,
  DialogPrimaryButton,
  DialogSpinner,
  VisuallyHidden,
} from "./EpeDialog";

// "Manage EPE operator": a case manager grants or removes EPE operator access (JustCue's presentation console)
// for anyone on the case. The people come from GET epe/cases/:caseId/participants; a switch PUTs (grant) or
// DELETEs (remove) epe/cases/:caseId/operators/:userId and the answer is the operators after the change.

const GROUPS = [
  { key: "caseManagement", label: "Case management" },
  { key: "tribunal", label: "Tribunal" },
  { key: "claimant", label: "Claimant" },
  { key: "respondent", label: "Respondent" },
  { key: "observer", label: "View-only" },
];

const GROUP_BY_SIDE = {
  registry: "caseManagement",
  tribunal: "tribunal",
  claimant: "claimant",
  respondent: "respondent",
};

const groupOf = (person) =>
  GROUPS.some((g) => g.key === person.group)
    ? person.group
    : GROUP_BY_SIDE[person.side] || "observer";

/** "Dr Meera Raghunathan" → "MR" */
const initialsOf = (name = "") => {
  const words = String(name)
    .replace(/^(dr|mr|mrs|ms|prof)\.?\s+/i, "")
    .split(/\s+/)
    .filter(Boolean);
  const first = words[0]?.[0] || "";
  const last = words.length > 1 ? words[words.length - 1][0] || "" : "";
  return (first + last).toUpperCase() || "?";
};

const displayName = (person) => person.name || person.email || "";

// Never empty: an empty (or JSON null) error body has no words of its own.
const errorText = (err) => (err && getErrorMessage(err)) || getErrorMessage({});

/** A stable avatar colour per person. */
const toneFor = (key = "") => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return h % 2 === 0 ? COLORS.BTN_GREEN : COLORS.COLOR_DARK;
};

const operatorsOf = (res, participants) =>
  Array.isArray(res?.operators)
    ? res.operators
    : participants
        .filter((p) => p.isOperator && p.id)
        .map((p) => ({ id: p.id, name: p.name }));

export default function ManageEpeOperatorModal({ caseId, onClose }) {
  const { access, refresh } = useEpeAccess();
  const [participants, setParticipants] = useState([]);
  // Until the participants arrive, the operators the case page already knows.
  const [operators, setOperators] = useState(() =>
    Array.isArray(access?.operators) ? access.operators : [],
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  // userId → { target }: the state a switch is moving to while its request is out.
  const [pending, setPending] = useState({});
  const [errors, setErrors] = useState({});
  const panelRef = useRef(null);
  const alive = useRef(true);
  // Changes go out one at a time, so every answer (the operators after it) is the newest.
  const queue = useRef(Promise.resolve());

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    let current = true;
    async function fetchParticipants() {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await EpeService.getParticipants(caseId);
        if (!current) return;
        const list = Array.isArray(res?.participants) ? res.participants : [];
        setParticipants(list);
        setOperators(operatorsOf(res, list));
      } catch (err) {
        if (current) setLoadError(errorText(err));
      } finally {
        if (current) setLoading(false);
      }
    }
    fetchParticipants();
    return () => {
      current = false;
    };
  }, [caseId, attempt]);

  // Once the people are listed, the first switch gets the focus (unless it has moved to a control already).
  useEffect(() => {
    const panel = panelRef.current;
    if (loading || !panel) return;
    const focused = document.activeElement;
    if (focused && focused !== panel && panel.contains(focused)) return;
    const firstSwitch = panel.querySelector('[role="switch"]:not([disabled])');
    if (firstSwitch) firstSwitch.focus();
  }, [loading]);

  const toggle = (person, target) => {
    const { id } = person;
    const name = displayName(person);
    setPending((p) => ({ ...p, [id]: { target } }));
    setErrors((e) => ({ ...e, [id]: undefined }));
    const send = async () => {
      try {
        const res = target
          ? await EpeService.grantOperator(caseId, id)
          : await EpeService.revokeOperator(caseId, id);
        if (!alive.current) return;
        setOperators((prev) =>
          Array.isArray(res?.operators)
            ? res.operators
            : target
            ? [...prev.filter((o) => o.id !== id), { id, name }]
            : prev.filter((o) => o.id !== id),
        );
      } catch (err) {
        if (!alive.current) return;
        setErrors((e) => ({
          ...e,
          [id]: `Couldn't ${target ? "grant" : "remove"} access for ${name}. ${errorText(err)}`,
        }));
      } finally {
        if (alive.current) {
          setPending((p) => {
            const next = { ...p };
            delete next[id];
            return next;
          });
        }
      }
    };
    queue.current = queue.current.then(send);
  };

  const close = () => {
    // The entry tab and the operator line on the case page read the access again after the last change.
    queue.current.then(() => refresh());
    onClose();
  };

  const operatorIds = new Set(operators.map((o) => o.id));
  const names = operators.map((o) => o.name || o.id);

  return (
    <EpeDialog
      id="epe-manage-operator"
      title="Manage EPE operator"
      description="EPE operators open the presentation console and control what everyone sees. Changes apply straight away, with no reload."
      onClose={close}
      panelRef={panelRef}
      footer={
        <DialogPrimaryButton type="button" onClick={close}>
          Done
        </DialogPrimaryButton>
      }
    >
      <StatusBox aria-live="polite">
        {names.length === 0 ? (
          "No one has EPE operator access yet."
        ) : (
          <>
            <strong>
              {names.length === 1
                ? "Current EPE operator: "
                : "Current EPE operators: "}
            </strong>
            {listJoin(names)}
          </>
        )}
      </StatusBox>

      {loading ? (
        <LoadingLine role="status">
          <DialogSpinner $color={COLORS.BTN_GREEN} $track="#e2e8f0" />
          Loading the people on this case…
        </LoadingLine>
      ) : loadError ? (
        <DialogNotice role="alert">
          <NoticeIcon aria-hidden="true">
            <ReportProblemOutlinedIcon style={{ fontSize: 16 }} />
          </NoticeIcon>
          <NoticeText>
            {loadError}
            <RetryButton type="button" onClick={() => setAttempt((a) => a + 1)}>
              Try again
            </RetryButton>
          </NoticeText>
        </DialogNotice>
      ) : (
        GROUPS.map((group) => {
          const people = participants.filter((p) => groupOf(p) === group.key);
          if (!people.length) return null;
          const headingId = `epe-group-${group.key}`;
          return (
            <Group key={group.key} role="group" aria-labelledby={headingId}>
              <GroupHeading id={headingId}>{group.label}</GroupHeading>
              <PeopleList>
                {people.map((person, index) => {
                  const rowId = `epe-person-${group.key}-${index}`;
                  const registered = !!person.id && person.registered !== false;
                  const rowPending = registered ? pending[person.id] : undefined;
                  return (
                    <ParticipantRow
                      key={person.id || rowId}
                      rowId={rowId}
                      person={person}
                      registered={registered}
                      checked={
                        rowPending
                          ? rowPending.target
                          : registered && operatorIds.has(person.id)
                      }
                      pending={!!rowPending}
                      error={registered ? errors[person.id] : undefined}
                      onToggle={(target) => toggle(person, target)}
                    />
                  );
                })}
              </PeopleList>
            </Group>
          );
        })
      )}

      <Footnote>
        More than one person can hold access. Only one of them controls the
        hearing at a time.
      </Footnote>
    </EpeDialog>
  );
}

function ParticipantRow({
  rowId,
  person,
  registered,
  checked,
  pending,
  error,
  onToggle,
}) {
  const name = displayName(person);
  const detailId = `${rowId}-detail`;
  const noteId = `${rowId}-note`;
  return (
    <PersonItem>
      <PersonLine>
        <Avatar
          aria-hidden="true"
          $bg={registered ? toneFor(person.id) : "#94a3b8"}
        >
          {initialsOf(name)}
        </Avatar>
        <PersonText>
          <PersonName>{name || "—"}</PersonName>
          <PersonDetail id={detailId}>
            {person.roleLabel}
            {pending && <VisuallyHidden>. Updating…</VisuallyHidden>}
          </PersonDetail>
          {person.email && <PersonMuted>{person.email}</PersonMuted>}
          {!registered && (
            <PersonMuted id={noteId}>Not registered on JustAct yet</PersonMuted>
          )}
        </PersonText>
        <SwitchButton
          type="button"
          role="switch"
          aria-checked={!!checked}
          aria-label={`EPE operator access for ${name}`}
          aria-describedby={registered ? detailId : `${detailId} ${noteId}`}
          aria-busy={pending || undefined}
          aria-disabled={pending || undefined}
          disabled={!registered}
          onClick={() => {
            if (registered && !pending) onToggle(!checked);
          }}
        >
          <SwitchTrack aria-hidden="true" />
          <SwitchKnob aria-hidden="true">
            {pending && (
              <DialogSpinner
                $size={10}
                $color={checked ? COLORS.BTN_GREEN : "#ffffff"}
                $track="transparent"
              />
            )}
          </SwitchKnob>
        </SwitchButton>
      </PersonLine>
      {error && (
        <DialogNotice role="alert" style={{ marginTop: 8 }}>
          <NoticeIcon aria-hidden="true">
            <ReportProblemOutlinedIcon style={{ fontSize: 16 }} />
          </NoticeIcon>
          <NoticeText>{error}</NoticeText>
        </DialogNotice>
      )}
    </PersonItem>
  );
}

const StatusBox = styled.p`
  margin: 0;
  padding: 10px 14px;
  border-radius: 10px;
  background: #e6f2f3;
  color: ${COLORS.COLOR_DARK};
  font-size: 13.5px;
  line-height: 1.5;
  font-family: ${theme.fonts.primaryFontRegular};

  strong {
    font-family: ${theme.fonts.primaryFontSemiBold};
    font-weight: normal;
  }
`;

const LoadingLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 2px;
  color: #64748b;
  font-size: 13px;
`;

const NoticeIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 2px;
  color: #c98a12;
`;

const NoticeText = styled.span`
  min-width: 0;
`;

const RetryButton = styled.button`
  margin-left: 8px;
  padding: 0;
  border: none;
  background: none;
  color: ${COLORS.BTN_GREEN};
  font-size: 13px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
`;

const GroupHeading = styled.h3`
  margin: 0 0 4px;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-weight: normal;
  letter-spacing: 0.02em;
  color: #64748b;
`;

const PeopleList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const PersonItem = styled.li`
  padding: 10px 0;

  & + & {
    border-top: 1px solid #eef1f6;
  }
`;

const PersonLine = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.span`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$bg};
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  font-family: ${theme.fonts.primaryFontSemiBold};
  user-select: none;
`;

const PersonText = styled.div`
  flex: 1;
  min-width: 0;
`;

const PersonName = styled.div`
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PersonDetail = styled.div`
  font-size: 12.5px;
  color: #475569;
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PersonMuted = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// role="switch": a 48 × 28 target. Off: white track with a grey border and knob; on: teal; pending: a spinner.
const SwitchButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  width: 48px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  &[aria-busy="true"] {
    cursor: progress;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.COLOR_DARK};
    outline-offset: 2px;
  }
`;

const SwitchTrack = styled.span`
  position: absolute;
  inset: 2px;
  border-radius: 999px;
  border: 2px solid #7f85a1;
  background: #ffffff;
  transition:
    background 0.2s ease,
    border-color 0.2s ease;

  ${SwitchButton}[aria-checked="true"] & {
    border-color: ${COLORS.BTN_GREEN};
    background: ${COLORS.BTN_GREEN};
  }

  ${SwitchButton}[aria-busy="true"] & {
    opacity: 0.6;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SwitchKnob = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #7f85a1;
  transform: translateX(7px);
  transition: transform 0.2s ease;

  ${SwitchButton}[aria-checked="true"] & {
    background: #ffffff;
    transform: translateX(25px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Footnote = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: #64748b;
`;
